import React from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Mail, Phone, MessageSquare, Pencil, Lock, LogIn, Bell, Gift, Trash2 } from 'lucide-react';
import { MemberAvatar } from '@/components/MemberAvatar';
import { SwipeableRow } from '@/components/SwipeableRow';

const ROLE_COLOR = {
  super_admin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  member: 'bg-slate-100 text-slate-700',
  viewer: 'bg-amber-100 text-amber-800',
};

const membershipColor = (status) => {
  if (status === 'active') return 'bg-emerald-100 text-emerald-700';
  if (status === 'trial') return 'bg-sky-100 text-sky-700';
  if (status === 'free') return 'bg-purple-100 text-purple-700';
  if (status === 'expired') return 'bg-red-100 text-red-700';
  return 'bg-slate-100 text-slate-500';
};

function relativeTime(ts) {
  if (!ts) return 'Never';
  const ageMs = Date.now() - new Date(ts).getTime();
  if (ageMs < 60_000) return 'Just now';
  const mins = Math.floor(ageMs / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(ageMs / 3_600_000);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(ageMs / 86_400_000);
  if (days < 30) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

export function MembersMobileCards({
  loading,
  members,
  emptyState,
  isAdmin,
  isSuperAdmin,
  userId,
  canEditMember,
  canEditPermissions,
  canImpersonate,
  setEditingMember,
  setPermissionsMember,
  handleImpersonate,
  handleNudge,
  handleToggleFree,
  handleSendPasswordReset,
  handleDelete,
}) {
  if (loading) {
    return (
      <div className="sm:hidden flex items-center justify-center py-16">
        <div className="animate-spin w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (members.length === 0) {
    return <div className="sm:hidden py-10 text-center text-sm text-slate-400">{emptyState || 'No members found'}</div>;
  }

  return (
    <div className="sm:hidden space-y-2" data-testid="members-mobile-cards">
      {members.map(m => {
        const isSelf = m.id === userId;
        const canEdit = canEditMember(m);
        const showActions = isAdmin && m.id !== userId && m.role !== 'super_admin';
        const stale = m.last_active_at && (Date.now() - new Date(m.last_active_at).getTime()) >= 7 * 86_400_000;
        return (
          <SwipeableRow
            key={m.id}
            className="border border-slate-200 rounded-sm bg-white"
            testId={`member-card-${m.id}`}
            onSwipeLeft={showActions ? () => handleDelete(m.id) : null}
            onSwipeRight={canEdit ? () => setEditingMember(m) : null}
            leftLabel="Remove"
            rightLabel="Edit"
          >
            <div className="flex items-start gap-3 p-3">
              <MemberAvatar userId={m.id} name={m.name} size="md" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {m.name}{isSelf && <span className="ml-1 text-[10px] text-slate-400">(you)</span>}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{m.email}</p>
                  </div>
                  {showActions && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-11 w-11 p-0 shrink-0 -mt-1 -mr-1" aria-label="Member actions" data-testid={`member-card-actions-${m.id}`}>
                          <MoreHorizontal className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canEdit && <DropdownMenuItem onClick={() => setEditingMember(m)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>}
                        {canEditPermissions(m) && <DropdownMenuItem onClick={() => setPermissionsMember(m)}><Lock className="w-4 h-4 mr-2" /> Permissions</DropdownMenuItem>}
                        {canImpersonate(m) && <DropdownMenuItem onClick={() => handleImpersonate(m)}><LogIn className="w-4 h-4 mr-2" /> Log in as {m.name.split(' ')[0]}</DropdownMenuItem>}
                        {canEdit && <DropdownMenuItem onClick={() => handleNudge(m)}><Bell className="w-4 h-4 mr-2" /> Nudge</DropdownMenuItem>}
                        {isSuperAdmin && <DropdownMenuItem onClick={() => handleToggleFree(m.id, m.membership_status)}><Gift className="w-4 h-4 mr-2" /> {m.membership_status === 'free' ? 'Remove Free' : 'Set Free'}</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => handleSendPasswordReset(m.id, m.email)}><Mail className="w-4 h-4 mr-2" /> Send Reset Email</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(m.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Remove</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${ROLE_COLOR[m.role] || 'bg-slate-100 text-slate-700'}`}>
                    {m.role === 'super_admin' ? 'Super Admin' : m.role}
                  </span>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${membershipColor(m.membership_status)}`}>
                    {m.membership_status || 'inactive'}
                  </span>
                  {m.last_active_at && (
                    <span className={`text-[10px] ${stale ? 'text-amber-700' : 'text-slate-500'}`}>
                      Active {relativeTime(m.last_active_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {(m.phone || m.email) && (
              <div className="flex items-stretch border-t border-slate-100">
                {m.phone && (
                  <a href={`tel:${m.phone}`} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 min-h-[44px] text-xs font-medium text-[#0047AB] active:bg-[#EFF6FF]" data-testid={`member-card-call-${m.id}`} aria-label={`Call ${m.name}`}>
                    <Phone className="w-3.5 h-3.5" /> Call
                  </a>
                )}
                {m.phone && (
                  <a href={`sms:${m.phone}`} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 min-h-[44px] text-xs font-medium text-emerald-700 active:bg-emerald-50 border-l border-slate-100" data-testid={`member-card-text-${m.id}`} aria-label={`Text ${m.name}`}>
                    <MessageSquare className="w-3.5 h-3.5" /> Text
                  </a>
                )}
                {m.email && (
                  <a href={`mailto:${m.email}`} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 min-h-[44px] text-xs font-medium text-slate-700 active:bg-slate-50 border-l border-slate-100" data-testid={`member-card-email-${m.id}`} aria-label={`Email ${m.name}`}>
                    <Mail className="w-3.5 h-3.5" /> Email
                  </a>
                )}
              </div>
            )}
          </SwipeableRow>
        );
      })}
    </div>
  );
}
